"use client";

import { localizeHref } from "@/lib/localize-href";
import { useState, useMemo, useCallback, useEffect, useRef, Suspense } from "react";
import { useTranslation } from "@/lib/i18n";
import {
  calcPrice as calcPriceLib,
  getBwSteps as getBwStepsLib,
  ENTERPRISE_THRESHOLD_EUR_MONTHLY,
  DWDM_CAPACITY_RANGE_LABEL,
  type PopRegion,
  type DarkFiberModel as PricingDarkFiberModel,
  type PriceResult,
} from "@/lib/pricing";
import { POP_COUNT_LABEL } from "@/lib/network-metrics";
import {
  provisioningForConfiguratorProduct,
  formatLeadTimeDays,
} from "@/data/provisioning-lead-times";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import {
  Zap,
  MapPin,
  Shield,
  ChevronRight,
  ChevronLeft,
  Cpu,
  Network,
  Cable,
  Globe,
  ArrowRight,
  Check,
  RotateCcw,
  Send,
  Package,
  FileText,
  Activity,
  Info,
  Link2,
  Layers,
  User,
  Mail,
  Phone,
  Building2,
  AlertTriangle,
  Anchor,
  Waves,
} from "lucide-react";
import { jsPDF } from "jspdf";
import { SOFIA_CONNECT_LOGO_PNG_B64 } from "@/lib/sofia-connect-logo-b64";
import { trackEvent as trackVercelEvent, getSessionId } from "@/lib/analytics";
import { isAnalyticsGranted } from "@/lib/consent";
import { PeerBenchmark } from "@/components/configurator/PeerBenchmark";
import { latencyMatrix } from "@/data/network-paths";
import { makeTr, CITY_BG } from "@/lib/tools-i18n";
import { CONFIGURATOR_BG, COUNTRY_BG } from "@/lib/tools-i18n/configurator";

/**
 * Dark-fibre custom-distance input bounds.
 *
 * 100 m is the smallest span the quoting engine models; below that
 * the deal collapses into an intra-datacentre cross-connect rather
 * than a dark fibre lease. 100 km is the largest single-segment
 * distance that fits inside a metro build without a regen — anything
 * beyond that is a national or cross-border project priced route by
 * route via sales, not a self-serve configurator estimate.
 */
const DARK_FIBRE_METERS_MIN = 100;
const DARK_FIBRE_METERS_MAX = 100_000;

/**
 * Clamp a user-supplied dark-fibre distance to the modelled envelope.
 * Rejects NaN by falling back to the lower bound (`Math.max` on `NaN`
 * returns `NaN`, so guard explicitly).
 */
function clampDarkFibreMeters(input: number): number {
  if (!Number.isFinite(input)) return DARK_FIBRE_METERS_MIN;
  return Math.min(DARK_FIBRE_METERS_MAX, Math.max(DARK_FIBRE_METERS_MIN, input));
}

/* ═════════════════════════════════════════════════════
   #11 — Analytics helper
   ═════════════════════════════════════════════════════ */
function trackEvent(event: string, data?: Record<string, unknown>) {
  // Google Analytics 4 (if gtag is loaded)
  if (typeof window !== "undefined") {
    const w = window as unknown as Record<string, unknown>;
    if (typeof w.gtag === "function") {
      (w.gtag as (...args: unknown[]) => void)("event", event, data);
    }
  }
  // Vercel Analytics — forward only serializable primitives
  try {
    const vercelProps: Record<string, string | number | boolean | null> = {};
    if (data) {
      for (const [k, v] of Object.entries(data)) {
        if (v === null || typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
          vercelProps[k] = v as string | number | boolean | null;
        } else {
          vercelProps[k] = String(v);
        }
      }
    }
    trackVercelEvent(event as Parameters<typeof trackVercelEvent>[0], vercelProps);
  } catch {
    // silent
  }
  // Console in dev
  if (process.env.NODE_ENV === "development") {
    console.log(`[SA Event] ${event}`, data);
  }
}

/* ═════════════════════════════════════════════════════
   PoP nodes
   ═════════════════════════════════════════════════════ */
interface PopNode {
  id: string;
  city: string;
  country: string;
  region: "europe" | "caucasus" | "middle-east" | "bulgaria-domestic";
  tier?: "hq" | "international" | "edge";
}

const POP_NODES: PopNode[] = [
  { id: "sofia", city: "Sofia", country: "Bulgaria", region: "europe", tier: "hq" },
  { id: "frankfurt", city: "Frankfurt", country: "Germany", region: "europe", tier: "international" },
  { id: "istanbul", city: "Istanbul", country: "Turkey", region: "europe", tier: "international" },
  { id: "athens", city: "Athens", country: "Greece", region: "europe", tier: "international" },
  { id: "london", city: "London", country: "UK", region: "europe", tier: "international" },
  { id: "tbilisi", city: "Tbilisi", country: "Georgia", region: "caucasus", tier: "international" },
  { id: "dubai", city: "Dubai", country: "UAE", region: "middle-east", tier: "international" },
  { id: "yerevan", city: "Yerevan", country: "Armenia", region: "caucasus", tier: "international" },
  { id: "agarak", city: "Agarak", country: "Armenia", region: "caucasus", tier: "international" },
  // Sofia Connect Edge — Domestic Bulgarian backbone (8 PoPs)
  { id: "plovdiv", city: "Plovdiv", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "stara-zagora", city: "Stara Zagora", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "burgas", city: "Burgas", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "varna", city: "Varna", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "balchik", city: "Balchik", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "dobrich", city: "Dobrich", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "montana", city: "Montana", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
  { id: "pleven", city: "Pleven", country: "Bulgaria", region: "bulgaria-domestic", tier: "edge" },
];

/* PoP coordinates for SVG map (#2) — International + clustered BG domestic around Sofia */
const POP_COORDS: Record<string, { x: number; y: number }> = {
  london:    { x: 130, y: 95 },
  frankfurt: { x: 210, y: 90 },
  sofia:     { x: 280, y: 135 },
  athens:    { x: 285, y: 165 },
  istanbul:  { x: 325, y: 140 },
  tbilisi:   { x: 410, y: 120 },
  yerevan:   { x: 420, y: 140 },
  agarak:    { x: 430, y: 158 },
  dubai:     { x: 445, y: 230 },
  // BG domestic Edge PoPs — subtle cluster near Sofia on the overview map
  plovdiv:      { x: 290, y: 141 },
  "stara-zagora": { x: 297, y: 142 },
  burgas:       { x: 306, y: 145 },
  varna:        { x: 308, y: 131 },
  balchik:      { x: 312, y: 128 },
  dobrich:      { x: 306, y: 127 },
  montana:      { x: 272, y: 129 },
  pleven:       { x: 282, y: 128 },
};

/* ═════════════════════════════════════════════════════
   Latency map (indicative RTT, ms)
   ═════════════════════════════════════════════════════ */
/* Derived from src/data/network-paths.ts — the single source for topology and
 * indicative RTT. Do not hand-edit numbers here. */
const LATENCY_MAP: Record<string, Record<string, number>> = latencyMatrix(
  POP_NODES.map((n) => n.id),
);

/* ═════════════════════════════════════════════════════
   Products
   ═════════════════════════════════════════════════════ */
type ProductId = "ip-transit" | "l2vpn" | "dwdm" | "dark-fiber";

// Descriptions describe reality per src/data/service-availability.ts:
// IP Transit is delivered from Sofia and Frankfurt only; L2VPN and DWDM
// from every PoP; Dark Fibre in the Sofia metro only today (national and
// cross-border corridors are on the roadmap — route-level enquiry only).
const PRODUCTS: { id: ProductId; label: string; desc: string; icon: typeof Zap }[] = [
  { id: "ip-transit", label: "IP Transit", desc: "Sofia & Frankfurt · 1G–400G", icon: Globe },
  { id: "l2vpn", label: "Ethernet / L2VPN", desc: `All ${POP_COUNT_LABEL} PoPs · Up to 10G`, icon: Network },
  { id: "dwdm", label: "Wavelengths (DWDM)", desc: `All ${POP_COUNT_LABEL} PoPs · ${DWDM_CAPACITY_RANGE_LABEL}`, icon: Zap },
  { id: "dark-fiber", label: "Dark Fiber", desc: "Sofia metro today", icon: Cable },
];

/* ═════════════════════════════════════════════════════
   Bandwidth steps — sourced from @/lib/pricing.
   The local alias keeps every existing call-site (getBwSteps(product))
   unchanged; the module is the single source of truth.
   ═════════════════════════════════════════════════════ */
const getBwSteps = getBwStepsLib;

/* ═════════════════════════════════════════════════════
   Available PoPs per product
   ═════════════════════════════════════════════════════ */
// Ground truth mirrors src/data/service-availability.ts.
const IP_TRANSIT_POP_IDS = new Set<string>(["sofia", "frankfurt"]);

function getAvailablePops(p: ProductId) {
  if (p === "ip-transit") return POP_NODES.filter((n) => IP_TRANSIT_POP_IDS.has(n.id));
  if (p === "dark-fiber") return [{ id: "sofia-metro", city: "Sofia metro", country: "Bulgaria", region: "europe" as const }];
  return POP_NODES;
}

/**
 * Full PoP list with per-item availability for the current product.
 * Unavailable options stay in the DOM (keyboard-reachable, SR-announced)
 * but are `disabled` with a human-readable reason, so a broken
 * configuration can never leave the form. Dark Fibre keeps its
 * single "Sofia metro" line item and does not go through this list.
 */
function getPopsWithAvailability(p: ProductId) {
  const okIds = new Set(getAvailablePops(p).map((n) => n.id));
  return POP_NODES.map((n) => ({
    ...n,
    available: okIds.has(n.id),
    reason: okIds.has(n.id)
      ? undefined
      : p === "ip-transit"
        ? "IP Transit is delivered only from Sofia and Frankfurt"
        : p === "dark-fiber"
          ? "Dark Fiber is in service only within the Sofia metro today — planned routes are on a roadmap enquiry path"
          : "Not currently available",
  }));
}

/* ═════════════════════════════════════════════════════
   Pricing tables + route helpers — sourced from @/lib/pricing.
   ETHERNET_PRICES, IP_TRANSIT_PRICES, DWDM_PRICES,
   DWDM_ROUTE_OVERRIDES, getRoutePriceL2VPN and getDwdmRouteOverride
   are re-exported by the import block above; call-sites downstream
   use them unchanged.
   ═════════════════════════════════════════════════════ */
type Region = PopRegion;
type DarkFiberModel = PricingDarkFiberModel;

/* ═════════════════════════════════════════════════════
   Submarine cable route detection
   ═════════════════════════════════════════════════════ */
const CCS_NODES = new Set(["tbilisi", "yerevan", "agarak", "balchik"]);
const ME_NODES = new Set(["dubai"]);

function getSubmarineCableInfo(origin: string, destination: string): { type: "ccs" | "me"; label: string; note: string } | null {
  const nodes = [origin, destination];
  const hasCCS = nodes.some((n) => CCS_NODES.has(n));
  const hasME = nodes.some((n) => ME_NODES.has(n));
  if (hasCCS) return {
    type: "ccs",
    label: "Caucasus Cable System (CCS)",
    note: "Route includes Black Sea submarine cable segment — Balchik (BG) to Poti (GE). Bypasses Türkiye terrestrial transit.",
  };
  if (hasME) return {
    type: "me",
    label: "Submarine Cable Route",
    note: "Route includes submarine cable segment through the Middle East corridor",
  };
  return null;
}

/* #5 — Dark Fiber distance presets */
const DARK_FIBER_PRESETS = [
  { value: 500, label: "500m" },
  { value: 1000, label: "1 km" },
  { value: 5000, label: "5 km" },
  { value: 10000, label: "10 km" },
  { value: -1, label: "Custom" },
];

/* ═════════════════════════════════════════════════════
   State
   ═════════════════════════════════════════════════════ */
interface ConfigState {
  product: ProductId;
  pop: string;
  origin: string;
  destination: string;
  bwIndex: number;
  darkFiberModel: DarkFiberModel;
  darkFiberMeters: number;
  darkFiberPreset: number;
  commitment: 12 | 24 | 36;
  burstModel: "flat" | "95th";
  redundancy: "single" | "protected" | "fully-redundant";
}

/* ═════════════════════════════════════════════════════
   Steps
   ═════════════════════════════════════════════════════ */
const STEP_DEFS = [
  { id: "product", labelKey: "naas_step_product", icon: Package },
  { id: "route", labelKey: "naas_step_route", icon: MapPin },
  { id: "capacity", labelKey: "naas_step_capacity", icon: Zap },
  { id: "terms", labelKey: "naas_step_terms", icon: Shield },
  { id: "review", labelKey: "naas_step_review", icon: Send },
] as const;

/* ═════════════════════════════════════════════════════
   Price calculation — thin adapter over @/lib/pricing.
   The lib takes a numeric capacity (bw); the configurator UI
   tracks a bwIndex into the current product's bandwidth steps.
   ═════════════════════════════════════════════════════ */

/* #7 — Enterprise pricing threshold (re-exported from @/lib/pricing). */
const ENTERPRISE_THRESHOLD = ENTERPRISE_THRESHOLD_EUR_MONTHLY;

function calcPrice(s: ConfigState): PriceResult {
  const bwSteps = getBwSteps(s.product);
  const bw = bwSteps[s.bwIndex]?.value ?? 0;
  return calcPriceLib({
    product: s.product,
    origin: s.origin,
    destination: s.destination,
    bw,
    darkFiberModel: s.darkFiberModel,
    darkFiberMeters: s.darkFiberMeters,
    commitment: s.commitment,
    burstModel: s.burstModel,
    redundancy: s.redundancy,
  });
}

/**
 * Provisioning-estimate cell for the configurator's summary / quote / CSV.
 *
 * Reads from the single source of truth in
 * `@/data/provisioning-lead-times`. Values there were confirmed by the
 * commercial owner on 15 September 2026.
 *
 * The previous implementation hardcoded three values that all disagreed
 * with the per-service Tech Specs tables on the /services/* pages:
 *   - dark-fiber returned "30–90 days" (Tech Specs said 15–30)
 *   - dwdm returned  "5–10 days"  (Tech Specs said 10–20)
 *   - ip-transit and l2vpn returned "~48 hours" (Tech Specs said 5–10 and 7–14)
 *
 * The "~48 hours" values in particular implied a same-week turn-up for
 * services that everywhere else were quoted at 5–14 days. Removed on
 * 15 September 2026 — do not restore from git history assuming it was
 * deliberate; it was wrong.
 */
function getProvisionTime(p: ProductId, lang: "en" | "bg" = "en") {
  const entry = provisioningForConfiguratorProduct(p);
  if (!entry) return lang === "bg" ? "Свържете се с продажби" : "Contact sales";
  return formatLeadTimeDays(entry, lang);
}

function generateQuoteRef(): string {
  const now = new Date();
  const year = now.getFullYear();
  const mmdd = String(now.getMonth() + 1).padStart(2, "0") + String(now.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `SC-${year}-${mmdd}-${rand}`;
}

/* ═════════════════════════════════════════════════════
   #10 — URL serialization for shareable links
   ═════════════════════════════════════════════════════ */
function stateToParams(s: ConfigState): string {
  const p = new URLSearchParams();
  p.set("p", s.product);
  if (s.product !== "dark-fiber" && s.product !== "ip-transit") {
    p.set("o", s.origin);
    p.set("d", s.destination);
  }
  if (s.product !== "dark-fiber") {
    p.set("bw", String(s.bwIndex));
    p.set("c", String(s.commitment));
    p.set("r", s.redundancy);
  }
  if (s.product === "ip-transit") p.set("burst", s.burstModel);
  if (s.product === "dark-fiber") {
    p.set("dfm", s.darkFiberModel);
    p.set("dfk", String(s.darkFiberMeters));
  }
  return p.toString();
}

function paramsToState(params: URLSearchParams): Partial<ConfigState> | null {
  const product = params.get("p") as ProductId | null;
  if (!product || !["ip-transit", "l2vpn", "dwdm", "dark-fiber"].includes(product)) return null;

  const partial: Partial<ConfigState> = { product };

  if (product !== "dark-fiber" && product !== "ip-transit") {
    const o = params.get("o");
    const d = params.get("d");
    if (o && POP_NODES.some((n) => n.id === o)) partial.origin = o;
    if (d && POP_NODES.some((n) => n.id === d)) partial.destination = d;
  }

  const bw = params.get("bw");
  if (bw) partial.bwIndex = Math.max(0, Math.min(Number(bw), getBwSteps(product).length - 1));

  const c = params.get("c");
  if (c && [12, 24, 36].includes(Number(c))) partial.commitment = Number(c) as 12 | 24 | 36;

  const r = params.get("r");
  if (r && ["single", "protected", "fully-redundant"].includes(r)) partial.redundancy = r as ConfigState["redundancy"];

  const burst = params.get("burst");
  if (burst && ["flat", "95th"].includes(burst)) partial.burstModel = burst as "flat" | "95th";

  if (product === "dark-fiber") {
    const dfm = params.get("dfm");
    if (dfm && ["monthly", "iru"].includes(dfm)) partial.darkFiberModel = dfm as DarkFiberModel;
    const dfk = params.get("dfk");
    if (dfk) partial.darkFiberMeters = clampDarkFibreMeters(Number(dfk));
  }

  return partial;
}

/* ═════════════════════════════════════════════════════
   #9 — Enhanced PDF generation
   ═════════════════════════════════════════════════════ */
function generatePDF(state: ConfigState, pricing: PriceResult, quoteRef: string) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = 210;
  const margin = 18;
  const contentW = pageW - margin * 2;

  const navy = [10, 15, 28] as [number, number, number];
  const teal = [0, 212, 170] as [number, number, number];
  const white = [255, 255, 255] as [number, number, number];
  const lightGray = [245, 247, 250] as [number, number, number];
  const medGray = [180, 188, 200] as [number, number, number];
  const darkText = [20, 28, 48] as [number, number, number];
  const mutedText = [100, 112, 130] as [number, number, number];

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  // ── Header bar (taller to accommodate real logo) ──
  const headerH = 46;
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageW, headerH, "F");

  // Company brand text (left side)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...teal);
  doc.text("Sofia Connect", margin, 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...medGray);
  doc.text("AS47872 · sofia-connect.net · +359 2 420 0300", margin, 25);
  doc.text("UIC 204636154 · VAT BG204636154", margin, 30);

  // "Service Quotation" title beneath the brand
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...white);
  doc.text("Service Quotation", margin, 39);

  // Real Sofia Connect logo — top right
  const logoSize = 22;
  const logoX = pageW - margin - logoSize;
  const logoY = (headerH - logoSize) / 2;
  try {
    doc.addImage(SOFIA_CONNECT_LOGO_PNG_B64, "PNG", logoX, logoY, logoSize, logoSize, undefined, "FAST");
  } catch {
    // Fallback — if logo fails to render, skip silently (not critical)
  }

  // Right-side metadata (Date / Ref / Validity) just below the logo
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...medGray);
  doc.text(`Date: ${dateStr}`, pageW - margin, headerH - 9, { align: "right" });
  doc.text(`Ref: ${quoteRef}`, pageW - margin, headerH - 5, { align: "right" });

  // ── Teal accent strip ──
  doc.setFillColor(...teal);
  doc.rect(0, headerH, pageW, 2, "F");

  let y = headerH + 10;

  // ── Configuration section ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...navy);
  doc.text("SERVICE CONFIGURATION", margin, y);
  y += 2;
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 5;

  const product = PRODUCTS.find((p) => p.id === state.product)!;
  const originCity = POP_NODES.find((n) => n.id === state.origin)?.city ?? state.origin;
  const destCity = POP_NODES.find((n) => n.id === state.destination)?.city ?? state.destination;
  const bwSteps = getBwSteps(state.product);
  const bw = bwSteps[state.bwIndex] ?? { value: 0, label: "—" };
  const latency = LATENCY_MAP[state.origin]?.[state.destination];

  type TableRow = [string, string];
  const configRows: TableRow[] = [["Product", product.label]];

  if (state.product === "dark-fiber") {
    configRows.push(["Location", "Bulgaria (National)"]);
    configRows.push(["Distance", `${state.darkFiberMeters.toLocaleString()} meters`]);
    configRows.push(["Pricing Model", state.darkFiberModel === "monthly" ? "Monthly Lease (€0.03/m/mo)" : "IRU 15 Years (€1.00/m + 4.5% O&M/yr)"]);
  } else if (state.product === "ip-transit") {
    configRows.push(["Location", "Sofia — Equinix SO1/SO2, Telepoint"]);
    configRows.push(["Capacity", bw.label]);
    configRows.push(["Commitment Term", `${state.commitment} months`]);
    configRows.push(["Burst Model", state.burstModel === "95th" ? "95th Percentile" : "Flat Rate"]);
    configRows.push(["Redundancy", state.redundancy === "single" ? "Single Path" : state.redundancy === "protected" ? "Protected 1+1" : "Fully Redundant"]);
    configRows.push(["Provisioning", getProvisionTime(state.product)]);
  } else {
    configRows.push(["Route", `${originCity} → ${destCity}`]);
    configRows.push(["Capacity", bw.label]);
    if (latency) configRows.push(["Estimated Latency", `~${latency} ms`]);
    configRows.push(["Commitment Term", `${state.commitment} months`]);
    configRows.push(["Redundancy", state.redundancy === "single" ? "Single Path" : state.redundancy === "protected" ? "Protected 1+1" : "Fully Redundant"]);
    configRows.push(["Provisioning", getProvisionTime(state.product)]);
    const cableInfo = getSubmarineCableInfo(state.origin, state.destination);
    if (cableInfo) {
      configRows.push(["Cable System", cableInfo.label]);
      configRows.push(["Route Note", cableInfo.note]);
    }
  }

  const rowH = 8;
  const colW = contentW / 2;

  configRows.forEach((row, idx) => {
    const rowY = y + idx * rowH;
    if (idx % 2 === 0) {
      doc.setFillColor(...lightGray);
      doc.rect(margin, rowY, contentW, rowH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...mutedText);
    doc.text(row[0], margin + 3, rowY + 5.2);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkText);
    doc.text(row[1], margin + colW + 3, rowY + 5.2);
  });

  y += configRows.length * rowH + 10;

  // ── Pricing Breakdown section ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...navy);
  doc.text("PRICING BREAKDOWN", margin, y);
  y += 2;
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 5;

  if (state.product === "dark-fiber") {
    const priceLabel = state.darkFiberModel === "monthly" ? "/month" : " IRU (15yr)";
    doc.setFillColor(...lightGray);
    doc.rect(margin, y, contentW, rowH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...mutedText);
    doc.text("Estimated Price", margin + 3, y + 5.2);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...darkText);
    doc.text(`€${pricing.price.toLocaleString()}${priceLabel}`, margin + colW + 3, y + 5.2);
    y += rowH + 10;
  } else {
    const pricingRows: TableRow[] = [["Base Monthly Rate", `€${pricing.base.toLocaleString()}`]];
    const discountPct = Math.round((1 - pricing.commitmentMultiplier) * 100);
    const burstDiscPct = Math.round((1 - pricing.burstMultiplier) * 100);
    const redundancySurchPct = Math.round((pricing.redundancyMultiplier - 1) * 100);

    if (discountPct > 0) {
      const saving = Math.round(pricing.base * (1 - pricing.commitmentMultiplier));
      pricingRows.push([`Commitment Discount (${state.commitment}mo)`, `-${discountPct}%  →  -€${saving.toLocaleString()}`]);
    }
    if (burstDiscPct > 0) {
      const saving = Math.round(pricing.base * pricing.commitmentMultiplier * (1 - pricing.burstMultiplier));
      pricingRows.push(["Burst Model Discount (95th %ile)", `-${burstDiscPct}%  →  -€${saving.toLocaleString()}`]);
    }
    if (redundancySurchPct > 0) {
      const surcharge = Math.round(pricing.base * pricing.commitmentMultiplier * pricing.burstMultiplier * (pricing.redundancyMultiplier - 1));
      pricingRows.push(["Redundancy Surcharge", `+${redundancySurchPct}%  →  +€${surcharge.toLocaleString()}`]);
    }

    pricingRows.forEach((row, idx) => {
      const rowY = y + idx * rowH;
      if (idx % 2 === 0) {
        doc.setFillColor(...lightGray);
        doc.rect(margin, rowY, contentW, rowH, "F");
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...mutedText);
      doc.text(row[0], margin + 3, rowY + 5.2);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkText);
      doc.text(row[1], margin + colW + 3, rowY + 5.2);
    });

    y += pricingRows.length * rowH + 3;
    doc.setDrawColor(...medGray);
    doc.setLineWidth(0.3);
    doc.line(margin, y, margin + contentW, y);
    y += 4;

    doc.setFillColor(...navy);
    doc.rect(margin, y, contentW, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...teal);
    doc.text("Total Monthly", margin + 3, y + 6.5);
    doc.setTextColor(...white);
    doc.text(`€${pricing.price.toLocaleString()}/month`, margin + colW + 3, y + 6.5);
    y += 10;

    const totalContract = pricing.price * state.commitment;
    doc.setFillColor(15, 22, 40);
    doc.rect(margin, y, contentW, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...medGray);
    doc.text(`Total Contract Value (${state.commitment} months)`, margin + 3, y + 6.5);
    doc.setTextColor(...teal);
    doc.text(`€${totalContract.toLocaleString()}`, margin + colW + 3, y + 6.5);
    y += 14;
  }

  // ── Watermark (#9) ──
  doc.setGState(doc.GState({ opacity: 0.04 }));
  doc.setFont("helvetica", "bold");
  doc.setFontSize(60);
  doc.setTextColor(...navy);
  doc.text("SOFIA CONNECT", pageW / 2, 180, { align: "center", angle: 30 });
  doc.setGState(doc.GState({ opacity: 1 }));

  // ── Footer with sales team contacts ──
  const footerY = 252;
  const footerH = 45;
  doc.setFillColor(...lightGray);
  doc.rect(0, footerY, pageW, footerH, "F");
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  doc.line(0, footerY, pageW, footerY);

  // Section header: "Contact our Sales Team"
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...navy);
  doc.text("CONTACT OUR SALES TEAM", margin, footerY + 7);
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.4);
  doc.line(margin, footerY + 9, margin + 55, footerY + 9);

  // Three columns of sales contacts
  const col1X = margin;
  const col2X = margin + contentW / 3;
  const col3X = margin + (contentW / 3) * 2;
  const contactsY = footerY + 15;

  // Column 1: Email
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...mutedText);
  doc.text("EMAIL", col1X, contactsY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...darkText);
  doc.text("sales@sofia-connect.net", col1X, contactsY + 5);
  doc.setFontSize(8);
  doc.setTextColor(...mutedText);
  doc.text("noc@sofia-connect.net (24/7)", col1X, contactsY + 10);

  // Column 2: Phone
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...mutedText);
  doc.text("PHONE", col2X, contactsY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...darkText);
  doc.text("+359 2 420 0300", col2X, contactsY + 5);
  doc.setFontSize(8);
  doc.setTextColor(...mutedText);
  doc.text("Mon–Fri 9:00–18:00 EET", col2X, contactsY + 10);

  // Column 3: Office
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...mutedText);
  doc.text("OFFICE", col3X, contactsY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...darkText);
  doc.text("192A Cherni Vrah Blvd, 13th fl.", col3X, contactsY + 5);
  doc.setFontSize(8);
  doc.setTextColor(...mutedText);
  doc.text("Sofia 1407, Bulgaria", col3X, contactsY + 10);

  // Legal footer strip (navy)
  const legalY = footerY + footerH - 13;
  doc.setFillColor(...navy);
  doc.rect(0, legalY, pageW, 13, "F");

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(...medGray);
  doc.text("Valid for 30 days from issue · Prices exclusive of VAT · Terms subject to contract", margin, legalY + 5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...teal);
  doc.text("Sofia Connect EAD · UIC 204636154 · VAT BG204636154", margin, legalY + 9.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...medGray);
  doc.text(`Quote Ref: ${quoteRef}`, pageW - margin, legalY + 9.5, { align: "right" });

  doc.save(`SofiaConnect-Quote-${quoteRef}.pdf`);
}

/**
 * Sprint #1 — Generate PDF and return as base64 string (no file save).
 * Reuses generatePDF doc construction but outputs datauristring instead.
 */
function generatePDFBase64(state: ConfigState, pricing: PriceResult, quoteRef: string): string {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = 210;
  const margin = 18;
  const contentW = pageW - margin * 2;

  const navy = [10, 15, 28] as [number, number, number];
  const teal = [0, 212, 170] as [number, number, number];
  const white = [255, 255, 255] as [number, number, number];
  const lightGray = [245, 247, 250] as [number, number, number];
  const medGray = [180, 188, 200] as [number, number, number];
  const darkText = [20, 28, 48] as [number, number, number];
  const mutedText = [100, 112, 130] as [number, number, number];

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  const headerH = 46;
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageW, headerH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...teal);
  doc.text("Sofia Connect", margin, 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...medGray);
  doc.text("AS47872 · sofia-connect.net · +359 2 420 0300", margin, 25);
  doc.text("UIC 204636154 · VAT BG204636154", margin, 30);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...white);
  doc.text("Service Quotation", margin, 39);

  const logoSize = 22;
  const logoX = pageW - margin - logoSize;
  const logoY = (headerH - logoSize) / 2;
  try {
    doc.addImage(SOFIA_CONNECT_LOGO_PNG_B64, "PNG", logoX, logoY, logoSize, logoSize, undefined, "FAST");
  } catch {
    // Fallback — skip silently
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...medGray);
  doc.text(`Date: ${dateStr}`, pageW - margin, headerH - 9, { align: "right" });
  doc.text(`Ref: ${quoteRef}`, pageW - margin, headerH - 5, { align: "right" });

  doc.setFillColor(...teal);
  doc.rect(0, headerH, pageW, 2, "F");

  let y = headerH + 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...navy);
  doc.text("SERVICE CONFIGURATION", margin, y);
  y += 2;
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 5;

  const product = PRODUCTS.find((p) => p.id === state.product)!;
  const originCity = POP_NODES.find((n) => n.id === state.origin)?.city ?? state.origin;
  const destCity = POP_NODES.find((n) => n.id === state.destination)?.city ?? state.destination;
  const bwStepsLocal = getBwSteps(state.product);
  const bwLocal = bwStepsLocal[state.bwIndex] ?? { value: 0, label: "—" };
  const latency = LATENCY_MAP[state.origin]?.[state.destination];

  type TableRow = [string, string];
  const configRows: TableRow[] = [["Product", product.label]];

  if (state.product === "dark-fiber") {
    configRows.push(["Location", "Bulgaria (National)"]);
    configRows.push(["Distance", `${state.darkFiberMeters.toLocaleString()} meters`]);
    configRows.push(["Pricing Model", state.darkFiberModel === "monthly" ? "Monthly Lease (€0.03/m/mo)" : "IRU 15 Years (€1.00/m + 4.5% O&M/yr)"]);
  } else if (state.product === "ip-transit") {
    configRows.push(["Location", "Sofia — Equinix SO1/SO2, Telepoint"]);
    configRows.push(["Capacity", bwLocal.label]);
    configRows.push(["Commitment Term", `${state.commitment} months`]);
    configRows.push(["Burst Model", state.burstModel === "95th" ? "95th Percentile" : "Flat Rate"]);
    configRows.push(["Redundancy", state.redundancy === "single" ? "Single Path" : state.redundancy === "protected" ? "Protected 1+1" : "Fully Redundant"]);
    configRows.push(["Provisioning", getProvisionTime(state.product)]);
  } else {
    configRows.push(["Route", `${originCity} → ${destCity}`]);
    configRows.push(["Capacity", bwLocal.label]);
    if (latency) configRows.push(["Estimated Latency", `~${latency} ms`]);
    configRows.push(["Commitment Term", `${state.commitment} months`]);
    configRows.push(["Redundancy", state.redundancy === "single" ? "Single Path" : state.redundancy === "protected" ? "Protected 1+1" : "Fully Redundant"]);
    configRows.push(["Provisioning", getProvisionTime(state.product)]);
    const cableInfo = getSubmarineCableInfo(state.origin, state.destination);
    if (cableInfo) {
      configRows.push(["Cable System", cableInfo.label]);
      configRows.push(["Route Note", cableInfo.note]);
    }
  }

  const rowH = 8;
  const colW = contentW / 2;
  void colW; // used in full generatePDF, kept for structural symmetry
  void darkText;
  void mutedText;

  configRows.forEach((row, idx) => {
    const rowY = y + idx * rowH;
    if (idx % 2 === 0) {
      doc.setFillColor(...lightGray);
      doc.rect(margin, rowY, contentW, rowH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...navy);
    doc.text(row[0], margin + 3, rowY + 5.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 70, 90);
    doc.text(row[1], margin + contentW / 2, rowY + 5.5);
  });

  y += configRows.length * rowH + 10;

  // Pricing section
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...navy);
  doc.text("PRICING", margin, y);
  y += 2;
  doc.setDrawColor(...teal);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 8;

  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...navy);
  doc.text(`€${pricing.price.toLocaleString()}${pricing.unit}`, margin, y);
  y += 10;

  // Footer
  const legalY = 277;
  doc.setFillColor(...navy);
  doc.rect(0, legalY, pageW, 20, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...medGray);
  doc.text("Sofia Connect · AS47872 · sofia-connect.net · +359 2 420 0300", margin, legalY + 5);
  doc.text("This quotation is indicative and subject to final engineering review. Valid 30 days.", margin, legalY + 9.5);
  doc.text(`Quote Ref: ${quoteRef}`, pageW - margin, legalY + 9.5, { align: "right" });

  return doc.output("datauristring").split(",")[1] ?? "";
}

/* ═════════════════════════════════════════════════════
   #13 — Animated price counter
   ═════════════════════════════════════════════════════ */
// AnimatedPrice removed with feat/hero-redesign (Task 4). It ran a 500ms
// count-up in the sidebar price card, but the redesigned terminal readout
// renders the price inline at 12–13px — an animated counter at that size
// reads as a glitch, not a live update. Screen-reader announcements are now
// driven by aria-live="polite" on the readout container itself.

/* ═════════════════════════════════════════════════════
   #2 — SVG Network Map for Route selection
   ═════════════════════════════════════════════════════ */
function NetworkMap({
  origin,
  destination,
  onSelectOrigin,
  onSelectDestination,
  selectingField,
  availableNodes,
}: {
  origin: string;
  destination: string;
  onSelectOrigin: (id: string) => void;
  onSelectDestination: (id: string) => void;
  selectingField: "origin" | "destination";
  availableNodes: string[];
}) {
  const { language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  const city = makeTr(language, CITY_BG);
  const country = makeTr(language, COUNTRY_BG);
  const latency = LATENCY_MAP[origin]?.[destination];

  return (
    <div className="rounded-xl border border-border bg-surface/50 p-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-3.5 h-3.5 text-accent" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{tr("Network Topology")}</span>
        {latency && (
          <span className="ml-auto text-[10px] font-mono text-accent">~{latency}ms</span>
        )}
      </div>
      <svg viewBox="60 50 430 220" preserveAspectRatio="xMidYMid meet" className="w-full h-auto min-h-[180px] sm:min-h-[240px] block" role="img" aria-label={tr("Network map showing PoP locations across Europe, Caucasus, and Middle East")}>
        {/* Background regions */}
        <rect x="60" y="50" width="430" height="220" rx="8" fill="var(--color-surface)" opacity="0.5" />

        {/* Connection line */}
        {origin && destination && origin !== destination && POP_COORDS[origin] && POP_COORDS[destination] && (
          <line
            x1={POP_COORDS[origin].x}
            y1={POP_COORDS[origin].y}
            x2={POP_COORDS[destination].x}
            y2={POP_COORDS[destination].y}
            stroke="var(--color-accent)"
            strokeWidth="2"
            strokeDasharray="6 3"
            opacity="0.6"
          >
            <animate attributeName="stroke-dashoffset" from="18" to="0" dur="1.5s" repeatCount="indefinite" />
          </line>
        )}

        {/* PoP nodes */}
        {availableNodes.map((nodeId) => {
          const coords = POP_COORDS[nodeId];
          const node = POP_NODES.find((n) => n.id === nodeId);
          if (!coords || !node) return null;

          const isOrigin = nodeId === origin;
          const isDestination = nodeId === destination;
          const isActive = isOrigin || isDestination;

          return (
            <g
              key={nodeId}
              className="cursor-pointer"
              onClick={() => {
                if (selectingField === "origin") onSelectOrigin(nodeId);
                else onSelectDestination(nodeId);
              }}
              role="button"
              tabIndex={0}
              aria-label={`${city(node.city)}, ${country(node.country)}${isOrigin ? ` (${tr("Origin")})` : ""}${isDestination ? ` (${tr("Destination")})` : ""}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (selectingField === "origin") onSelectOrigin(nodeId);
                  else onSelectDestination(nodeId);
                }
              }}
            >
              {/* Pulse ring for active nodes */}
              {isActive && (
                <circle cx={coords.x} cy={coords.y} r="10" fill="none" stroke="var(--color-accent)" strokeWidth="1" opacity="0.4">
                  <animate attributeName="r" values="6;14;6" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite" />
                </circle>
              )}

              {/* Node dot */}
              <circle
                cx={coords.x}
                cy={coords.y}
                r={isActive ? 5 : 3.5}
                fill={isOrigin ? "var(--color-accent)" : isDestination ? "#f59e0b" : "var(--color-muted-foreground)"}
                stroke={isActive ? "var(--color-background)" : "none"}
                strokeWidth={isActive ? 1.5 : 0}
              />

              {/* Label — hide BG domestic labels unless active to avoid overlap */}
              {(isActive || node.region !== "bulgaria-domestic") && (
                <text
                  x={coords.x}
                  y={coords.y - 9}
                  textAnchor="middle"
                  fill={isActive ? "var(--color-foreground)" : "var(--color-muted-foreground)"}
                  fontSize={isActive ? 9 : 8}
                  fontWeight={isActive ? 600 : 400}
                  fontFamily="system-ui, sans-serif"
                >
                  {city(node.city)}
                </text>
              )}
            </g>
          );
        })}

        {/* Legend */}
        <circle cx="75" cy="255" r="3" fill="var(--color-accent)" />
        <text x="82" y="258" fill="var(--color-muted-foreground)" fontSize="7" fontFamily="system-ui, sans-serif">{tr("Origin")}</text>
        <circle cx="120" cy="255" r="3" fill="#f59e0b" />
        <text x="127" y="258" fill="var(--color-muted-foreground)" fontSize="7" fontFamily="system-ui, sans-serif">{tr("Destination")}</text>
      </svg>
    </div>
  );
}

/* ═════════════════════════════════════════════════════
   #14 — Visual route diagram for Review step
   ═════════════════════════════════════════════════════ */
function RouteVisual({ origin, destination, latency, product }: { origin: string; destination: string; latency?: number; product: ProductId }) {
  const { language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  const city = makeTr(language, CITY_BG);
  const country = makeTr(language, COUNTRY_BG);
  const originCity = city(POP_NODES.find((n) => n.id === origin)?.city ?? origin);
  const destCity = city(POP_NODES.find((n) => n.id === destination)?.city ?? destination);

  if (product === "dark-fiber") {
    return (
      <div className="flex items-center justify-center gap-3 py-3">
        <div className="px-3 py-1.5 rounded-lg bg-accent/10 text-accent text-xs font-bold">{country("Bulgaria")}</div>
        <Cable className="w-4 h-4 text-muted-foreground" />
        <div className="text-xs text-muted-foreground">{tr("Dark Fiber")}</div>
      </div>
    );
  }

  if (product === "ip-transit") {
    return (
      <div className="flex items-center justify-center gap-3 py-3">
        <div className="px-3 py-1.5 rounded-lg bg-accent/10 text-accent text-xs font-bold">{city("Sofia")}</div>
        <div className="flex items-center gap-1.5">
          <div className="w-12 h-px bg-accent/40" />
          <Globe className="w-3.5 h-3.5 text-accent" />
          <div className="w-12 h-px bg-accent/40" />
        </div>
        <div className="text-xs text-muted-foreground">{tr("Internet")}</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 py-3">
      <div className="px-3 py-1.5 rounded-lg bg-accent/10 text-accent text-xs font-bold">{originCity}</div>
      <div className="flex items-center gap-1">
        <div className="w-8 h-px bg-accent/40" />
        <div className="flex flex-col items-center">
          <ArrowRight className="w-3.5 h-3.5 text-accent" />
          {latency && <span className="text-[9px] font-mono text-accent mt-0.5">~{latency}ms</span>}
        </div>
        <div className="w-8 h-px bg-accent/40" />
      </div>
      <div className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-500 text-xs font-bold">{destCity}</div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════
   Submarine Cable Badge
   ═════════════════════════════════════════════════════ */
function SubmarineCableBadge({ origin, destination, compact = false }: { origin: string; destination: string; compact?: boolean }) {
  const { language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  const info = getSubmarineCableInfo(origin, destination);
  if (!info) return null;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 text-[9px] font-bold">
        <Waves className="w-2.5 h-2.5" /> {info.type === "ccs" ? "CCS" : tr("Subsea")}
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 rounded-lg bg-sky-500/5 border border-sky-500/20 flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0 mt-0.5">
        <Anchor className="w-3.5 h-3.5 text-sky-400" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-semibold text-foreground">{tr(info.label)}</span>
          <span className="px-1.5 py-px rounded text-[8px] font-bold uppercase tracking-wider bg-sky-500/15 text-sky-400">
            Submarine
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">{tr(info.note)}</p>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════
   #6 — DWDM region pricing tooltip
   ═════════════════════════════════════════════════════ */
function RegionPriceTooltip({ region }: { region: Region }) {
  const { language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  const explanations: Record<Region, string> = {
    europe: "Direct fiber routes via Sofia Connect backbone",
    caucasus: "International terrestrial segment via Turkey/Georgia included",
    "middle-east": "Extended international segment via subsea & terrestrial routes included",
    "bulgaria-domestic": "Sofia Connect Edge — domestic Bulgarian backbone, sub-10ms from Sofia",
  };

  return (
    <div className="group relative inline-flex items-center ml-1.5">
      <Info className="w-3 h-3 text-muted-foreground cursor-help" aria-label={tr("Pricing info")} />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-foreground text-background text-[10px] leading-relaxed whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50 shadow-lg">
        {tr(explanations[region])}
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-foreground" />
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════
   The Component
   ═════════════════════════════════════════════════════ */
export function UnifiedConfigurator() {
  return (
    <Suspense fallback={null}>
      <UnifiedConfiguratorInner />
    </Suspense>
  );
}

function UnifiedConfiguratorInner() {
  const { t, language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  const city = makeTr(language, CITY_BG);
  const country = makeTr(language, COUNTRY_BG);
  const leadTime = (p: ProductId) => getProvisionTime(p, language);
  const privacyHref = language === "bg" ? "/bg/legal/privacy" : "/legal/privacy";
  const router = useRouter();
  // URL params are read inside the mount effects below via window.location.
  // useSearchParams() would make this whole component client-only on the
  // statically rendered home page, dropping the configurator from the HTML.
  const [step, setStep] = useState(0);
  const [quoteRef] = useState(() => generateQuoteRef());
  const [routeSelectField, setRouteSelectField] = useState<"origin" | "destination">("origin");

  // Mobile sticky price bar visibility — show only when configurator section is in viewport
  const sectionRef = useRef<HTMLElement>(null);
  const [inViewport, setInViewport] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setInViewport(entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px -80px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Tell the other fixed controls (dock, SC-NIA launcher) that the mobile
  // price bar is up, so they sit above it instead of covering its button.
  // The CSS side is .fab-lift in globals.css (below lg only).
  const stickyBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (inViewport) {
      root.dataset.stickyBar = "on";
      const h = stickyBarRef.current?.offsetHeight ?? 0;
      if (h) root.style.setProperty("--sticky-bar-h", `${h}px`);
    } else {
      delete root.dataset.stickyBar;
    }
    return () => {
      delete root.dataset.stickyBar;
    };
  }, [inViewport]);

  /* #4 — Compare mode */
  const [compareConfig, setCompareConfig] = useState<ConfigState | null>(null);
  const [showCompare, setShowCompare] = useState(false);

  /* #1 — Inline contact form state */
  const [contactForm, setContactForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [submitStatus, setSubmitStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  /* Sprint #1 — Session ID (persisted in sessionStorage, shared with analytics).
   *
   * F1 fix (2026-09-11): route the read/write through getSessionId in
   * src/lib/analytics.ts so the same consent gate applies here as in
   * the analytics helper. Before consent is granted this returns ""
   * and nothing is written to sessionStorage. On grant the key is
   * created on next call (which happens automatically from the tracked
   * analytics events themselves); until then any local generator we
   * need for correlation stays in-memory only (see fallback below).
   *
   * Consequences we accept:
   *   - Under Reject or before any choice, `sessionIdRef.current` is
   *     an in-memory UUID for the current tab only; nothing about it
   *     leaves the browser (trackVercelEvent early-returns on the same
   *     gate), so the value is effectively a local correlator.
   *   - Once consent is granted, the ref is re-synced to the persisted
   *     `sa_session_id`, so analytics events downstream continue to
   *     share one id per session.
   */
  const sessionIdRef = useRef<string>("");
  useEffect(() => {
    const persisted = getSessionId();
    if (persisted) {
      sessionIdRef.current = persisted;
    } else if (!sessionIdRef.current) {
      // In-memory only — not written to sessionStorage.
      sessionIdRef.current = crypto.randomUUID();
    }
    // Re-sync when consent state changes at runtime (Accept from the
    // banner or the preference centre): pull the newly-persisted id
    // so analytics events use the same value getSessionId returns.
    const onConsent = () => {
      const p = getSessionId();
      if (p) sessionIdRef.current = p;
    };
    window.addEventListener("sc-consent-change", onConsent);
    return () => window.removeEventListener("sc-consent-change", onConsent);
  }, []);

  /* Sprint #2 — Track whether we successfully submitted (to suppress abandon event) */
  const didSubmitRef = useRef(false);

  /* #10 — Copy link feedback */
  const [linkCopied, setLinkCopied] = useState(false);

  const [state, setState] = useState<ConfigState>({
    product: "dwdm",
    pop: "sofia",
    origin: "sofia",
    destination: "frankfurt",
    bwIndex: 0,
    darkFiberModel: "monthly",
    darkFiberMeters: 1000,
    darkFiberPreset: 1000,
    commitment: 12,
    burstModel: "flat",
    redundancy: "single",
  });

  /* #10 — Restore from URL params on mount */
  /* Also handles mini-pricer deep-link: ?origin=X&destination=Y&capacity=Z */
  const miniPricerInitRef = useRef(false);
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    // Standard share-link params (?p=, ?o=, ?d=, etc.)
    const partial = paramsToState(searchParams);
    if (partial) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: hydrate configurator from URL once on mount
      setState((prev) => ({ ...prev, ...partial }));
    }

    // Mini-pricer deep-link params — run only once
    if (miniPricerInitRef.current) return;
    miniPricerInitRef.current = true;

    const miniOrigin = searchParams.get("origin");
    const miniDest = searchParams.get("destination");
    const miniCapacity = searchParams.get("capacity") as "1G" | "10G" | "100G" | "400G" | null;

    if (miniOrigin || miniDest || miniCapacity) {
      const update: Partial<ConfigState> = {};

      if (miniOrigin && POP_NODES.some((n) => n.id === miniOrigin)) {
        update.origin = miniOrigin;
        update.pop = miniOrigin;
      }
      if (miniDest && POP_NODES.some((n) => n.id === miniDest)) {
        update.destination = miniDest;
      }
      if (miniCapacity) {
        const capacityBwMap: Record<string, number> = {
          "1G": 0,
          "10G": 1,
          "100G": 2,
          "400G": 3,
        };
        const bwIdx = capacityBwMap[miniCapacity];
        if (bwIdx !== undefined) {
          // Use dwdm as default product for route-based capacity
          update.bwIndex = bwIdx;
          if (!partial?.product) update.product = "dwdm";
        }
      }

      if (Object.keys(update).length > 0) {
        setState((prev) => ({ ...prev, ...update }));
        // Advance to capacity step (step 2 in dwdm: product=0, route=1, capacity=2)
        if (miniCapacity) setStep(2);
      }
    }
  }, []);

  /* #10 — Persist to localStorage, but only when analytics consent has been
     granted. The stored payload is UI-only — the picked product, PoPs,
     bandwidth and term — with NO personal data (name/company/email/phone
     live in a separate contactForm state that is never persisted). Even
     so, a returning-user convenience flag is not "strictly necessary"
     under ePrivacy: users can still complete a fresh configuration or
     share a link via the ?p= URL parameter, which is the primary
     handover path. Gating the write behind isAnalyticsGranted() also
     ensures clearNonEssentialStorage() (invoked on withdrawal) sweeps
     this key correctly. */
  useEffect(() => {
    if (!isAnalyticsGranted()) return;
    try {
      localStorage.setItem("sc-configurator", JSON.stringify(state));
    } catch {}
  }, [state]);

  /* #10 — Restore from localStorage on mount (lower priority than URL).
     Same consent gate as the writer — if the user has not accepted the
     analytics category, the state simply starts at defaults. Any value
     that WAS written under a previous consent decision is removed by
     clearNonEssentialStorage() on withdrawal, so there is nothing stale
     to read here in that case. */
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("p")) return; // URL params take priority
    if (!isAnalyticsGranted()) return;
    try {
      const saved = localStorage.getItem("sc-configurator");
      if (saved) {
        const parsed = JSON.parse(saved);
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: restore persisted state once on mount
        setState((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, []);

  const bwSteps = useMemo(() => getBwSteps(state.product), [state.product]);
  const availablePops = useMemo(() => getAvailablePops(state.product), [state.product]);
  // Full PoP list with per-item disabled flag + reason — used by the
  // Route step selects so unavailable options are visible, keyboard-
  // reachable and labelled rather than hidden. Dark Fibre stays a
  // single "Bulgaria" line item, so we skip it there.
  const popsWithAvailability = useMemo(
    () => (state.product === "dark-fiber" ? availablePops.map((n) => ({ ...n, available: true, reason: undefined })) : getPopsWithAvailability(state.product)),
    [state.product, availablePops],
  );
  const pricing = useMemo(() => calcPrice(state), [state]);
  const bw = useMemo(
    () => bwSteps[state.bwIndex] ?? { value: 0, label: "—" },
    [bwSteps, state.bwIndex],
  );

  /* Compare pricing */
  const comparePricing = useMemo(() => compareConfig ? calcPrice(compareConfig) : null, [compareConfig]);

  const needsRouteStep = state.product === "l2vpn" || state.product === "dwdm";
  const needsCapacityStep = state.product !== "dark-fiber";
  const needsTermsStep = state.product !== "dark-fiber";

  type StepDef = { id: string; labelKey: string; icon: typeof Zap };
  const activeSteps = useMemo((): StepDef[] => {
    const steps: StepDef[] = [STEP_DEFS[0]];
    if (needsRouteStep) steps.push(STEP_DEFS[1]);
    if (needsCapacityStep) steps.push(STEP_DEFS[2]);
    if (needsTermsStep) steps.push(STEP_DEFS[3]);
    steps.push(STEP_DEFS[4]);
    return steps;
  }, [needsRouteStep, needsCapacityStep, needsTermsStep]);

  const totalSteps = activeSteps.length;
  const currentStepDef = activeSteps[step];

  const canProceed = useCallback(() => {
    if (currentStepDef?.id === "route" && state.origin === state.destination) return false;
    return true;
  }, [currentStepDef, state.origin, state.destination]);

  const nextStep = () => {
    if (step < totalSteps - 1 && canProceed()) {
      const nextStepIndex = step + 1;
      const nextStepDef = activeSteps[nextStepIndex];
      trackEvent("sa_step_advance", { step: currentStepDef?.id, stepIndex: step }); // #11
      trackEvent("sa_step_view", { // Sprint #2
        step: nextStepDef?.id,
        stepIndex: nextStepIndex,
        product: state.product,
        bandwidth: bw?.label,
        origin: state.origin,
        destination: state.destination,
      });
      setStep(nextStepIndex);
    }
  };
  const prevStep = () => {
    if (step > 0) {
      const prevStepIndex = step - 1;
      const prevStepDef = activeSteps[prevStepIndex];
      trackEvent("sa_step_back", { step: currentStepDef?.id, stepIndex: step }); // #11
      trackEvent("sa_step_view", { // Sprint #2
        step: prevStepDef?.id,
        stepIndex: prevStepIndex,
        product: state.product,
        bandwidth: bw?.label,
        origin: state.origin,
        destination: state.destination,
      });
      setStep(prevStepIndex);
    }
  };

  const reset = () => {
    trackEvent("sa_reset"); // #11
    setStep(0);
    setSubmitStatus("idle");
    setContactForm({ name: "", company: "", email: "", phone: "" });
    setContactErrors({});
    setState({
      product: "dwdm",
      pop: "sofia",
      origin: "sofia",
      destination: "frankfurt",
      bwIndex: 0,
      darkFiberModel: "monthly",
      darkFiberMeters: 1000,
      darkFiberPreset: 1000,
      commitment: 12,
      burstModel: "flat",
      redundancy: "single",
    });
  };

  /* Sprint #2+6 — Abandon tracking: fire on beforeunload / visibilitychange if user leaves mid-funnel */
  useEffect(() => {
    const fireAbandon = () => {
      if (didSubmitRef.current) return; // successful submit — not an abandon
      if (step <= 0) return; // hasn't started the funnel yet
      trackEvent("sa_step_abandon", {
        step: currentStepDef?.id,
        stepIndex: step,
        product: state.product,
        bandwidth: bw?.label,
        origin: state.origin,
        destination: state.destination,
      });

      // Sprint #6 — fire retarget webhook for high-value abandons
      const isHighValueBandwidth = bw?.label === "100G" || bw?.label === "400G";
      const isHighValuePrice = pricing.price >= 15000;
      const isEnterpriseProduct = state.product === "dwdm" || state.product === "dark-fiber";
      if (isHighValueBandwidth || isHighValuePrice || isEnterpriseProduct) {
        // fire-and-forget — do not block page navigation
        fetch("/api/retarget/high-value", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: sessionIdRef.current,
            product: state.product,
            origin: state.origin,
            destination: state.destination,
            bandwidth: bw?.label,
            estimatedPrice: pricing.price,
            lastStep: currentStepDef?.id,
          }),
          keepalive: true, // allow request to outlive page
        }).catch(() => {}); // fire-and-forget
      }
    };

    const handleBeforeUnload = () => fireAbandon();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") fireAbandon();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [step, state.product, state.origin, state.destination, bw, currentStepDef, pricing.price]);  

  const handleProductChange = (newProduct: ProductId) => {
    trackEvent("sa_product_select", { product: newProduct }); // #11
    setState((s) => {
      const newPops = getAvailablePops(newProduct);
      const okIds = new Set(newPops.map((n) => n.id));
      // If the currently selected origin/destination isn't deliverable
      // for the new product, reset it so we never submit an impossible
      // quote. IP Transit resets to Sofia→Frankfurt (the only pair).
      const nextOrigin =
        newProduct === "ip-transit"
          ? "sofia"
          : okIds.has(s.origin)
            ? s.origin
            : (newPops[0]?.id ?? s.origin);
      const nextDestination =
        newProduct === "ip-transit"
          ? "frankfurt"
          : okIds.has(s.destination) && s.destination !== nextOrigin
            ? s.destination
            : (newPops.find((n) => n.id !== nextOrigin)?.id ?? s.destination);
      return {
        ...s,
        product: newProduct,
        bwIndex: 0,
        pop: newPops[0]?.id ?? s.pop,
        origin: nextOrigin,
        destination: nextDestination,
      };
    });
  };

  /* Build the message for contact form / email */
  const buildMessage = useCallback(() => {
    const p = PRODUCTS.find((pr) => pr.id === state.product)!;
    const originCity = POP_NODES.find((n) => n.id === state.origin)?.city ?? state.origin;
    const destCity = POP_NODES.find((n) => n.id === state.destination)?.city ?? state.destination;
    const latency = LATENCY_MAP[state.origin]?.[state.destination];

    const redundancyLabel = state.redundancy === "single"
      ? "Single Path"
      : state.redundancy === "protected"
        ? "Protected 1+1 (+30%)"
        : "Fully Redundant (+60%)";

    const lines: string[] = [
      `Service Inquiry — ${p.label}`,
      ``,
      `Product: ${p.label}`,
    ];

    if (state.product === "dark-fiber") {
      lines.push(`Location: Bulgaria`);
      lines.push(`Distance: ${state.darkFiberMeters.toLocaleString()} meters`);
      lines.push(`Pricing Model: ${state.darkFiberModel === "monthly" ? "Monthly Lease (€0.03/m/mo)" : "IRU 15 Years (€1.00/m + 4.5% O&M/yr)"}`);
      lines.push(`Estimated Price: €${pricing.price.toLocaleString()}${pricing.unit}`);
      if (state.darkFiberModel === "iru") {
        const om = Math.round(state.darkFiberMeters * 1.0 * 0.045);
        lines.push(`Annual O&M: €${om.toLocaleString()}/year`);
      }
    } else if (state.product === "ip-transit") {
      lines.push(`Location: Sofia (Equinix SO1/SO2, Telepoint)`);
      lines.push(`Capacity: ${bw.label}`);
      lines.push(`Commitment Term: ${state.commitment} months`);
      lines.push(`Burst Model: ${state.burstModel === "95th" ? "95th Percentile" : "Flat Rate"}`);
      lines.push(`Redundancy: ${redundancyLabel}`);
      lines.push(`Estimated Price: €${pricing.price.toLocaleString()}/mo`);
    } else {
      lines.push(`Route: ${originCity} → ${destCity}`);
      lines.push(`Capacity: ${bw.label}`);
      if (latency) lines.push(`Estimated Latency: ~${latency} ms`);
      lines.push(`Commitment Term: ${state.commitment} months`);
      lines.push(`Redundancy: ${redundancyLabel}`);
      lines.push(`Estimated Price: ${pricing.isFrom ? "From " : ""}€${pricing.price.toLocaleString()}/mo`);
      const emailCableInfo = getSubmarineCableInfo(state.origin, state.destination);
      if (emailCableInfo) {
        lines.push(`Cable System: ${emailCableInfo.label}`);
        lines.push(`Route Note: ${emailCableInfo.note}`);
      }
    }

    lines.push(``);
    lines.push(`Provisioning Estimate: ${getProvisionTime(state.product)}`);
    lines.push(`Quote Ref: ${quoteRef}`);
    lines.push(``);
    lines.push(`Please provide a formal quote for the above configuration.`);

    return lines.join("\n");
  }, [state, bw, pricing, quoteRef]);

  /* #1 — Inline submit via API (no redirect) */
  const handleInlineSubmit = async () => {
    const errors: Record<string, string> = {};
    if (!contactForm.name || contactForm.name.length < 2) errors.name = "Name is required";
    if (!contactForm.company) errors.company = "Company is required";
    if (!contactForm.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) errors.email = "Valid email is required";

    if (Object.keys(errors).length) {
      setContactErrors(errors);
      return;
    }

    setSubmitStatus("submitting");
    trackEvent("sa_quote_submit", { // #11 Sprint #2 enriched
      product: state.product,
      price: pricing.price,
      origin: state.origin,
      destination: state.destination,
      bandwidth: bw.label,
      sla_tier: state.redundancy,
      term_months: state.commitment,
      burst_ratio: pricing.burstMultiplier,
    });

    try {
      const message = buildMessage();
      const serviceMap: Record<ProductId, string> = {
        "ip-transit": "ip-transit",
        "l2vpn": "ethernet-l2vpn",
        "dwdm": "wavelengths-dwdm",
        "dark-fiber": "dark-fiber",
      };

      // Sprint #1 — generate PDF base64 and build configurationSnapshot
      let pdfBase64: string | undefined;
      try {
        pdfBase64 = generatePDFBase64(state, pricing, quoteRef);
      } catch (pdfErr) {
        console.warn("PDF generation failed:", pdfErr);
      }

      const configurationSnapshot = {
        product: state.product,
        origin: state.origin,
        destination: state.destination,
        bandwidth: bw.label,
        slaTier: state.redundancy,
        termMonths: state.commitment,
        price: pricing.price,
        burstMultiplier: pricing.burstMultiplier,
        commitmentMultiplier: pricing.commitmentMultiplier,
      };

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: contactForm.name,
          company: contactForm.company,
          email: contactForm.email,
          phone: contactForm.phone || undefined,
          department: "sales",
          service: serviceMap[state.product],
          message,
          // Sprint #1 extras
          configurationSnapshot,
          pdfBase64,
          quoteRef,
          sessionId: sessionIdRef.current,
        }),
      });

      if (!res.ok) throw new Error("Submit failed");
      didSubmitRef.current = true; // Sprint #2 — suppress abandon event
      setSubmitStatus("success");
      trackEvent("sa_quote_success", { product: state.product }); // #11
    } catch {
      setSubmitStatus("error");
      trackEvent("sa_quote_error", { product: state.product }); // #11
    }
  };

  /* Navigate to contact form (fallback) */
  const sendToContactForm = () => {
    trackEvent("sa_send_to_contact", { product: state.product }); // #11
    const message = buildMessage();
    const serviceMap: Record<ProductId, string> = {
      "ip-transit": "ip-transit",
      "l2vpn": "ethernet-l2vpn",
      "dwdm": "wavelengths-dwdm",
      "dark-fiber": "dark-fiber",
    };
    const params = new URLSearchParams({
      department: "sales",
      service: serviceMap[state.product],
      message,
    });
    router.push(localizeHref(`/contact/?${params.toString()}`, language));
  };

  /* Download PDF */
  const downloadPDF = () => {
    trackEvent("sa_pdf_download", { product: state.product, price: pricing.price, quoteRef }); // #11
    generatePDF(state, pricing, quoteRef);
  };

  /* #10 — Copy shareable link */
  const copyShareableLink = () => {
    const url = `${window.location.origin}/#solution-architect?${stateToParams(state)}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      trackEvent("sa_link_copied", { product: state.product }); // #11
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  /* Latency for current route */
  const currentLatency = LATENCY_MAP[state.origin]?.[state.destination];

  /* Discount check */
  const hasDiscount = pricing.commitmentMultiplier < 1 || pricing.burstMultiplier < 1;
  const totalSavePct = Math.round((1 - (pricing.commitmentMultiplier * pricing.burstMultiplier)) * 100);

  /* #7 — Enterprise threshold check */
  const isEnterprise = pricing.price >= ENTERPRISE_THRESHOLD;

  /* #6 — Get region for DWDM tooltip */
  const currentRegion = POP_NODES.find((n) => n.id === state.origin)?.region ?? "europe";

  /* #4 — Save current config for comparison */
  const saveForComparison = () => {
    setCompareConfig({ ...state });
    setShowCompare(true);
    trackEvent("sa_compare_save"); // #11
  };

  return (
    <section ref={sectionRef} id="configurator" className="relative py-28 overflow-hidden scroll-mt-24">
      {/* Legacy anchor for backwards compatibility */}
      <span id="solution-architect" className="absolute -top-24" aria-hidden="true" />
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-surface/30 to-background pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
          <span className="text-[10px] font-mono uppercase tracking-widest text-accent">{t('naas_eyebrow')}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2">{t('naas_heading')}</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-sm leading-relaxed">
            {t('naas_subtitle')}
          </p>
        </motion.div>

        {/* ── #15 — Labeled Progress Bar ── */}
        <div className="mb-8" role="navigation" aria-label={tr("Configuration steps")}>
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {activeSteps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isCompleted = i < step;
              return (
                <div key={s.id} className="flex items-center flex-1 last:flex-none">
                  <button
                    onClick={() => { if (i <= step) setStep(i); }}
                    disabled={i > step}
                    className={`flex flex-col items-center gap-1 transition-all ${isActive ? "scale-105" : ""}`}
                    aria-label={`${tr("Step")} ${i + 1}: ${t(s.labelKey as Parameters<typeof t>[0])}${isCompleted ? ` (${tr("completed")})` : isActive ? ` (${tr("current")})` : ""}`}
                    aria-current={isActive ? "step" : undefined}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      isCompleted
                        ? "bg-accent text-on-accent"
                        : isActive
                          ? "bg-accent/15 text-accent ring-2 ring-accent/40"
                          : "bg-surface text-muted-foreground"
                    }`}>
                      {isCompleted ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    </div>
                    <span className={`text-[9px] font-medium whitespace-nowrap ${
                      isActive ? "text-accent" : isCompleted ? "text-foreground" : "text-muted-foreground"
                    }`}>
                      {t(s.labelKey as Parameters<typeof t>[0])}
                    </span>
                  </button>
                  {/* Connector line */}
                  {i < activeSteps.length - 1 && (
                    <div className={`flex-1 h-px mx-2 mt-[-14px] transition-colors ${
                      i < step ? "bg-accent" : "bg-border"
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main content grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid lg:grid-cols-[1fr_280px] gap-6"
        >
          {/* ── Left panel — steps ── */}
          <div className="bento-card min-h-[500px] flex flex-col">
            <div className="relative z-10 flex-1 flex flex-col">
              <AnimatePresence mode="wait">
                {/* ── STEP: Product ── */}
                {currentStepDef?.id === "product" && (
                  <motion.div key="product" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="flex-1">
                    <StepHeader icon={Package} title="Choose a Product" subtitle="Select the connectivity service you need" />
                    <PeerBenchmark variant="product" context={{ product: state.product }} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                      {PRODUCTS.map((p) => {
                        const Icon = p.icon;
                        const isSelected = state.product === p.id;
                        return (
                          <button
                            key={p.id}
                            onClick={() => handleProductChange(p.id)}
                            className={`group relative p-4 rounded-xl border text-left transition-all ${
                              isSelected
                                ? "border-accent bg-accent/5 ring-1 ring-accent/30"
                                : "border-border bg-surface hover:border-accent/30 hover:bg-surface"
                            }`}
                            aria-pressed={isSelected}
                            aria-label={`${tr(p.label)}: ${tr(p.desc)}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                                isSelected ? "bg-accent text-on-accent" : "bg-accent/10 text-accent"
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="font-semibold text-sm text-foreground">{tr(p.label)}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{tr(p.desc)}</div>
                              </div>
                              {isSelected && (
                                <div className="absolute top-3 right-3">
                                  <Check className="w-4 h-4 text-accent" />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* #5 — Dark Fiber presets */}
                    {state.product === "dark-fiber" && (
                      <div className="mt-6 space-y-4">
                        <StepHeader icon={Cable} title="Fiber Distance" subtitle="How many meters of dark fiber do you need?" />

                        {/* Preset chips */}
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={tr("Distance presets")}>
                          {DARK_FIBER_PRESETS.map((preset) => {
                            const isActive = state.darkFiberPreset === preset.value;
                            return (
                              <button
                                key={preset.value}
                                onClick={() => {
                                  setState((s) => ({
                                    ...s,
                                    darkFiberPreset: preset.value,
                                    ...(preset.value > 0 ? { darkFiberMeters: preset.value } : {}),
                                  }));
                                }}
                                className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-all ${
                                  isActive
                                    ? "bg-accent text-on-accent"
                                    : "bg-surface border border-border text-foreground hover:border-accent/40"
                                }`}
                                role="radio"
                                aria-checked={isActive}
                              >
                                {tr(preset.label)}
                              </button>
                            );
                          })}
                        </div>

                        {/* Custom input (shown when "Custom" is selected or always as secondary) */}
                        {state.darkFiberPreset === -1 && (
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              min={DARK_FIBRE_METERS_MIN}
                              max={DARK_FIBRE_METERS_MAX}
                              step={100}
                              inputMode="numeric"
                              value={state.darkFiberMeters}
                              onChange={(e) => {
                                // Cast, clamp on the way in so paste of e.g.
                                // "3000000" cannot escape the modelled
                                // envelope. Empty input falls back to min
                                // rather than 0 to keep the quoting engine
                                // in-range.
                                const raw = e.target.value;
                                if (raw === "") {
                                  setState((s) => ({ ...s, darkFiberMeters: DARK_FIBRE_METERS_MIN }));
                                  return;
                                }
                                setState((s) => ({
                                  ...s,
                                  darkFiberMeters: clampDarkFibreMeters(Number(raw)),
                                }));
                              }}
                              onFocus={(e) => e.currentTarget.select()}
                              className="w-32 px-3 py-2 rounded-lg border border-border bg-surface text-foreground text-sm font-mono focus:border-accent focus:outline-none"
                              aria-label={tr("Custom distance in meters")}
                            />
                            <span className="text-xs text-muted-foreground">{tr("meters")}</span>
                          </div>
                        )}

                        {/* Pricing model */}
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{tr("Pricing Model")}</div>
                          <div className="grid grid-cols-2 gap-2">
                            {([["monthly", "Monthly Lease", "€0.03/m/mo"], ["iru", "IRU 15 Years", "€1.00/m + O&M"]] as const).map(([val, label, sub]) => (
                              <button
                                key={val}
                                onClick={() => setState((s) => ({ ...s, darkFiberModel: val }))}
                                className={`p-3 rounded-lg border text-left transition-all ${
                                  state.darkFiberModel === val
                                    ? "border-accent bg-accent/5"
                                    : "border-border bg-surface hover:border-accent/30"
                                }`}
                                role="radio"
                                aria-checked={state.darkFiberModel === val}
                              >
                                <div className="text-xs font-semibold text-foreground">{tr(label)}</div>
                                <div className="text-[10px] text-muted-foreground mt-0.5">{tr(sub)}</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* ── STEP: Route ── */}
                {currentStepDef?.id === "route" && (
                  <motion.div key="route" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="flex-1">
                    <StepHeader icon={MapPin} title="Select Route" subtitle="Choose origin and destination PoPs" />
                    <PeerBenchmark variant="route" context={{ origin: state.origin, destination: state.destination }} />

                    {/* #2 — SVG Network Map */}
                    <NetworkMap
                      origin={state.origin}
                      destination={state.destination}
                      onSelectOrigin={(id) => {
                        setState((s) => ({ ...s, origin: id, pop: id }));
                        setRouteSelectField("destination");
                      }}
                      onSelectDestination={(id) => setState((s) => ({ ...s, destination: id }))}
                      selectingField={routeSelectField}
                      availableNodes={availablePops.map((p) => p.id)}
                    />

                    <div className="flex items-center gap-2 mb-3">
                      <button
                        onClick={() => setRouteSelectField("origin")}
                        className={`text-[10px] font-medium px-2 py-1 rounded ${routeSelectField === "origin" ? "bg-accent/15 text-accent" : "text-muted-foreground"}`}
                      >
                        {tr("Selecting: Origin")}
                      </button>
                      <button
                        onClick={() => setRouteSelectField("destination")}
                        className={`text-[10px] font-medium px-2 py-1 rounded ${routeSelectField === "destination" ? "bg-amber-500/15 text-amber-500" : "text-muted-foreground"}`}
                      >
                        {tr("Selecting: Destination")}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block" id="origin-label">{tr("Origin")}</label>
                        <select
                          value={state.origin}
                          onChange={(e) => setState((s) => ({ ...s, origin: e.target.value, pop: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface text-foreground text-sm focus:border-accent focus:outline-none"
                          aria-labelledby="origin-label"
                        >
                          {popsWithAvailability.map((n) => {
                            const disabled = !n.available || n.id === state.destination;
                            const label = !n.available
                              ? `${city(n.city)}, ${country(n.country)} — ${tr("unavailable")}`
                              : `${city(n.city)}, ${country(n.country)}`;
                            return (
                              <option
                                key={n.id}
                                value={n.id}
                                disabled={disabled}
                                aria-label={n.reason ? `${label}. ${tr(n.reason)}` : label}
                                title={n.reason ? tr(n.reason) : undefined}
                              >
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block" id="dest-label">{tr("Destination")}</label>
                        <select
                          value={state.destination}
                          onChange={(e) => setState((s) => ({ ...s, destination: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-lg border border-border bg-surface text-foreground text-sm focus:border-accent focus:outline-none"
                          aria-labelledby="dest-label"
                        >
                          {popsWithAvailability.map((n) => {
                            const disabled = !n.available || n.id === state.origin;
                            const label = !n.available
                              ? `${city(n.city)}, ${country(n.country)} — ${tr("unavailable")}`
                              : `${city(n.city)}, ${country(n.country)}`;
                            return (
                              <option
                                key={n.id}
                                value={n.id}
                                disabled={disabled}
                                aria-label={n.reason ? `${label}. ${tr(n.reason)}` : label}
                                title={n.reason ? tr(n.reason) : undefined}
                              >
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {currentLatency && (
                      <div className="mt-3 p-3 rounded-lg bg-accent/5 border border-accent/20 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-accent" />
                        <span className="text-sm text-foreground font-medium">~{currentLatency} ms</span>
                        <span className="text-xs text-muted-foreground">{tr("estimated round-trip latency")}</span>
                      </div>
                    )}

                    {/* Submarine cable badge — route step */}
                    {state.product !== "dark-fiber" && state.product !== "ip-transit" && (
                      <SubmarineCableBadge origin={state.origin} destination={state.destination} />
                    )}

                    {/* #6 — DWDM region pricing note */}
                    {state.product === "dwdm" && currentRegion !== "europe" && (
                      <div className="mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <div>
                          <span className="text-xs text-foreground font-medium">
                            {currentRegion === "caucasus" ? tr("Caucasus routing") : tr("Middle East routing")}
                          </span>
                          <RegionPriceTooltip region={currentRegion as Region} />
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {currentRegion === "caucasus"
                              ? tr("Pricing includes international terrestrial segment via Turkey/Georgia.")
                              : tr("Pricing includes extended international segment via subsea & terrestrial routes.")}
                          </p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* ── STEP: Capacity ── */}
                {currentStepDef?.id === "capacity" && (
                  <motion.div key="capacity" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="flex-1">
                    <StepHeader icon={Zap} title="Select Capacity" subtitle={state.product === "ip-transit" ? "Sofia — Equinix SO1/SO2, Telepoint" : "Choose your bandwidth"} />
                    <PeerBenchmark variant="bandwidth" context={{ bandwidth: bwSteps[state.bwIndex]?.label }} />

                    {/* #3 — Chip selector for bandwidth */}
                    <div className="mt-2">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3" id="bw-label">{tr("Bandwidth")}</div>
                      <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="bw-label">
                        {bwSteps.map((b, i) => {
                          const isActive = state.bwIndex === i;
                          const stepPrice = (() => {
                            const tmpState = { ...state, bwIndex: i };
                            return calcPrice(tmpState).price;
                          })();
                          return (
                            <button
                              key={i}
                              onClick={() => {
                                setState((s) => ({ ...s, bwIndex: i }));
                                trackEvent("sa_bandwidth_select", { bandwidth: b.label, price: stepPrice }); // #11
                              }}
                              className={`relative px-4 py-2.5 rounded-xl border text-center transition-all min-w-[70px] ${
                                isActive
                                  ? "border-accent bg-accent/10 ring-1 ring-accent/30"
                                  : "border-border bg-surface hover:border-accent/30"
                              }`}
                              role="radio"
                              aria-checked={isActive}
                              aria-label={`${b.label} — €${stepPrice}${tr("/mo")}`}
                            >
                              <div className={`text-sm font-bold font-mono ${isActive ? "text-accent" : "text-foreground"}`}>{b.label}</div>
                              <div className="text-[9px] font-mono text-muted-foreground mt-0.5">€{stepPrice.toLocaleString()}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* IP Transit burst model */}
                    {state.product === "ip-transit" && (
                      <div className="mt-6">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{tr("Burst Model")}</div>
                        <div className="grid grid-cols-2 gap-3">
                          {([["flat", "Flat Rate", "Fixed monthly cost"], ["95th", "95th Percentile", "Pay for actual usage, save ~15%"]] as const).map(([val, label, sub]) => (
                            <button
                              key={val}
                              onClick={() => setState((s) => ({ ...s, burstModel: val }))}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                state.burstModel === val
                                  ? "border-accent bg-accent/5"
                                  : "border-border bg-surface hover:border-accent/30"
                              }`}
                              role="radio"
                              aria-checked={state.burstModel === val}
                            >
                              <div className="text-xs font-semibold text-foreground">{tr(label)}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{tr(sub)}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* ── STEP: Terms ── */}
                {currentStepDef?.id === "terms" && (
                  <motion.div key="terms" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="flex-1">
                    <StepHeader icon={Shield} title="Contract Terms" subtitle="Choose commitment and redundancy options" />
                    <PeerBenchmark variant="provisioning" context={{}} />

                    <div className="space-y-6">
                      {/* Commitment */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2" id="commit-label">{tr("Commitment Period")}</div>
                        <div className="grid grid-cols-3 gap-3" role="radiogroup" aria-labelledby="commit-label">
                          {([12, 24, 36] as const).map((months) => {
                            const discount = months === 24 ? 10 : months === 36 ? 18 : 0;
                            return (
                              <button
                                key={months}
                                onClick={() => setState((s) => ({ ...s, commitment: months }))}
                                className={`p-3 rounded-xl border text-center transition-all ${
                                  state.commitment === months
                                    ? "border-accent bg-accent/5 ring-1 ring-accent/30"
                                    : "border-border bg-surface hover:border-accent/30"
                                }`}
                                role="radio"
                                aria-checked={state.commitment === months}
                                aria-label={`${months} ${tr("months")}${discount > 0 ? `, ${discount}% ${tr("discount")}` : ""}`}
                              >
                                <div className="text-lg font-bold font-mono text-foreground">{months}</div>
                                <div className="text-[10px] text-muted-foreground">{tr("months")}</div>
                                {discount > 0 && (
                                  <div className="mt-1 text-[10px] font-bold text-emerald-400">-{discount}%</div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Redundancy */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2" id="redund-label">{tr("Redundancy")}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-labelledby="redund-label">
                          {([
                            ["single", "Single Path", "Standard connectivity"],
                            ["protected", "Protected 1+1", "+30% · Automatic failover"],
                            ["fully-redundant", "Fully Redundant", "+60% · Diverse paths"],
                          ] as const).map(([val, label, sub]) => (
                            <button
                              key={val}
                              onClick={() => setState((s) => ({ ...s, redundancy: val }))}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                state.redundancy === val
                                  ? "border-accent bg-accent/5 ring-1 ring-accent/30"
                                  : "border-border bg-surface hover:border-accent/30"
                              }`}
                              role="radio"
                              aria-checked={state.redundancy === val}
                            >
                              <div className="text-xs font-semibold text-foreground">{tr(label)}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{tr(sub)}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* ── STEP: Review & Send ── */}
                {currentStepDef?.id === "review" && (
                  <motion.div key="review" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.35 }} className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-eyebrow">{tr("Your Configuration")}</span>
                        <h3 className="text-lg font-bold text-foreground mt-0.5">{tr(PRODUCTS.find((p) => p.id === state.product)!.label)}</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* #4 — Compare button */}
                        <button
                          onClick={saveForComparison}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent transition-colors"
                          title={tr("Save this configuration for comparison")}
                        >
                          <Layers className="w-3.5 h-3.5" /> {tr("Compare")}
                        </button>
                        <button onClick={reset} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                          <RotateCcw className="w-3.5 h-3.5" /> {tr("Start Over")}
                        </button>
                      </div>
                    </div>

                    {/* #14 — Visual route diagram */}
                    <RouteVisual origin={state.origin} destination={state.destination} latency={currentLatency} product={state.product} />

                    {/* Configuration Summary */}
                    <div className="rounded-xl border border-border bg-surface p-4 mb-4">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">{tr("Configuration Summary")}</div>
                      <div className="space-y-2">
                        {state.product === "dark-fiber" ? (
                          <>
                            <SummaryRow label="Location" value="Bulgaria" />
                            <SummaryRow label="Distance" value={`${state.darkFiberMeters.toLocaleString()} m`} />
                            <SummaryRow label="Model" value={state.darkFiberModel === "monthly" ? "Monthly Lease" : "IRU 15 Years"} />
                            <SummaryRow label="Provisioning" value={leadTime(state.product)} />
                          </>
                        ) : state.product === "ip-transit" ? (
                          <>
                            <SummaryRow label="Location" value="Sofia — Equinix SO1/SO2" />
                            <SummaryRow label="Capacity" value={bw.label} />
                            <SummaryRow label="Commitment" value={`${state.commitment} ${tr("months")}`} />
                            <SummaryRow label="Burst Model" value={state.burstModel === "95th" ? "95th Percentile" : "Flat Rate"} />
                            <SummaryRow label="Redundancy" value={state.redundancy === "single" ? "Single Path" : state.redundancy === "protected" ? "Protected 1+1" : "Fully Redundant"} />
                            <SummaryRow label="Provisioning" value={leadTime(state.product)} />
                          </>
                        ) : (
                          <>
                            <SummaryRow label="Route" value={`${city(POP_NODES.find((n) => n.id === state.origin)?.city ?? state.origin)} → ${city(POP_NODES.find((n) => n.id === state.destination)?.city ?? state.destination)}`} />
                            <SummaryRow label="Capacity" value={bw.label} />
                            {currentLatency && <SummaryRow label="Latency" value={`~${currentLatency} ms`} accent />}
                            <SummaryRow label="Commitment" value={`${state.commitment} ${tr("months")}`} />
                            <SummaryRow label="Redundancy" value={state.redundancy === "single" ? "Single Path" : state.redundancy === "protected" ? "Protected 1+1" : "Fully Redundant"} />
                            <SummaryRow label="Provisioning" value={leadTime(state.product)} />
                            {getSubmarineCableInfo(state.origin, state.destination) && (
                              <div className="mt-2 pt-2 border-t border-border/50">
                                <div className="flex items-center gap-1.5 text-sky-400">
                                  <Waves className="w-3 h-3" />
                                  <span className="text-[10px] font-bold">{tr(getSubmarineCableInfo(state.origin, state.destination)!.label)}</span>
                                </div>
                                <p className="text-[9px] text-muted-foreground mt-0.5 ml-4.5">
                                  {tr(getSubmarineCableInfo(state.origin, state.destination)!.note)}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Pricing Breakdown */}
                    {state.product !== "dark-fiber" && (
                      <div className="rounded-xl border border-border bg-surface p-4 mb-4">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">{tr("Pricing Breakdown")}</div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{tr("Base monthly")}</span>
                            <span className="font-mono text-foreground">€{pricing.base.toLocaleString()}</span>
                          </div>
                          {pricing.commitmentMultiplier < 1 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-emerald-400">{tr("Commitment discount")} ({state.commitment} {tr("mo")})</span>
                              <span className="font-mono text-emerald-400">-{Math.round((1 - pricing.commitmentMultiplier) * 100)}% → -€{Math.round(pricing.base * (1 - pricing.commitmentMultiplier)).toLocaleString()}</span>
                            </div>
                          )}
                          {pricing.burstMultiplier < 1 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-emerald-400">{tr("95th percentile model")}</span>
                              <span className="font-mono text-emerald-400">-15% → -€{Math.round(pricing.base * pricing.commitmentMultiplier * 0.15).toLocaleString()}</span>
                            </div>
                          )}
                          {pricing.redundancyMultiplier > 1 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-amber-400">{tr("Redundancy surcharge")}</span>
                              <span className="font-mono text-amber-400">+{Math.round((pricing.redundancyMultiplier - 1) * 100)}% → +€{Math.round(pricing.base * pricing.commitmentMultiplier * pricing.burstMultiplier * (pricing.redundancyMultiplier - 1)).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="border-t border-border pt-2 mt-2 flex justify-between">
                            <span className="font-semibold text-foreground">{tr("Total monthly")}</span>
                            <span className="font-bold font-mono text-accent text-base">€{pricing.price.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{tr("Total contract")} ({state.commitment} {tr("mo")})</span>
                            <span className="font-mono text-foreground">€{(pricing.price * state.commitment).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-xs text-muted mb-4">
                      {tr("Quote ref:")} <span className="font-mono text-accent">{quoteRef}</span> · {tr("Valid 30 days · Indicative pricing, final quote may vary.")}
                    </p>

                    {/* #7 — Enterprise badge */}
                    {isEnterprise && (
                      <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-accent/5 to-amber-500/5 border border-accent/20 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent/15 flex items-center justify-center">
                          <Cpu className="w-4 h-4 text-accent" />
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-foreground">{tr("Enterprise Configuration")}</div>
                          <div className="text-[10px] text-muted-foreground">{tr("Custom pricing available — our sales team can offer volume discounts for this configuration.")}</div>
                        </div>
                      </div>
                    )}

                    {/* #1 — Inline Contact Form */}
                    {submitStatus === "success" ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center"
                      >
                        <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                          <Check className="w-6 h-6 text-emerald-400" />
                        </div>
                        <h4 className="text-lg font-semibold text-foreground">{tr("Quote Request Sent")}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {tr("Our sales team will respond within 24 hours.")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {tr("Reference:")} <span className="font-mono text-accent">{quoteRef}</span>
                        </p>
                      </motion.div>
                    ) : (
                      <>
                        <div className="rounded-xl border border-border bg-surface p-4 mb-4">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">{tr("Your Contact Details")}</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] text-muted-foreground mb-1 block" htmlFor="sa-name">
                                {tr("Name")} <span className="text-red-400">*</span>
                              </label>
                              <div className="relative">
                                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <input
                                  id="sa-name"
                                  type="text"
                                  value={contactForm.name}
                                  onChange={(e) => { setContactForm((f) => ({ ...f, name: e.target.value })); setContactErrors((e2) => { const n = { ...e2 }; delete n.name; return n; }); }}
                                  className={`w-full pl-8 pr-3 py-2 rounded-lg border text-sm bg-surface text-foreground focus:outline-none ${
                                    contactErrors.name ? "border-red-400 focus:border-red-400" : "border-border focus:border-accent"
                                  }`}
                                  placeholder={tr("John Smith")}
                                  aria-invalid={!!contactErrors.name}
                                  aria-describedby={contactErrors.name ? "sa-name-err" : undefined}
                                />
                              </div>
                              {contactErrors.name && <p id="sa-name-err" className="text-[10px] text-red-400 mt-0.5">{tr(contactErrors.name)}</p>}
                            </div>
                            <div>
                              <label className="text-[10px] text-muted-foreground mb-1 block" htmlFor="sa-company">
                                {tr("Company")} <span className="text-red-400">*</span>
                              </label>
                              <div className="relative">
                                <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <input
                                  id="sa-company"
                                  type="text"
                                  value={contactForm.company}
                                  onChange={(e) => { setContactForm((f) => ({ ...f, company: e.target.value })); setContactErrors((e2) => { const n = { ...e2 }; delete n.company; return n; }); }}
                                  className={`w-full pl-8 pr-3 py-2 rounded-lg border text-sm bg-surface text-foreground focus:outline-none ${
                                    contactErrors.company ? "border-red-400 focus:border-red-400" : "border-border focus:border-accent"
                                  }`}
                                  placeholder={tr("Acme Corp")}
                                  aria-invalid={!!contactErrors.company}
                                />
                              </div>
                              {contactErrors.company && <p className="text-[10px] text-red-400 mt-0.5">{tr(contactErrors.company)}</p>}
                            </div>
                            <div>
                              <label className="text-[10px] text-muted-foreground mb-1 block" htmlFor="sa-email">
                                {tr("Email")} <span className="text-red-400">*</span>
                              </label>
                              <div className="relative">
                                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <input
                                  id="sa-email"
                                  type="email"
                                  value={contactForm.email}
                                  onChange={(e) => { setContactForm((f) => ({ ...f, email: e.target.value })); setContactErrors((e2) => { const n = { ...e2 }; delete n.email; return n; }); }}
                                  className={`w-full pl-8 pr-3 py-2 rounded-lg border text-sm bg-surface text-foreground focus:outline-none ${
                                    contactErrors.email ? "border-red-400 focus:border-red-400" : "border-border focus:border-accent"
                                  }`}
                                  placeholder="john@acme.com"
                                  aria-invalid={!!contactErrors.email}
                                />
                              </div>
                              {contactErrors.email && <p className="text-[10px] text-red-400 mt-0.5">{tr(contactErrors.email)}</p>}
                            </div>
                            <div>
                              <label className="text-[10px] text-muted-foreground mb-1 block" htmlFor="sa-phone">{tr("Phone")}</label>
                              <div className="relative">
                                <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <input
                                  id="sa-phone"
                                  type="tel"
                                  value={contactForm.phone}
                                  onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))}
                                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-border text-sm bg-surface text-foreground focus:border-accent focus:outline-none"
                                  placeholder="+359 2 420 0300"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* GDPR Art. 13 notice — same pattern as
                            ContactForm / NewsletterForm / InsightsSubscribeForm.
                            Placed above the CTA row so it is visible before
                            the user submits the quote request. */}
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          {t("form_notice_configurator")}{" "}
                          <a href={privacyHref} className="underline hover:text-foreground">
                            {t("form_notice_privacy_link")}
                          </a>
                          .
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={downloadPDF}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-medium border border-border bg-surface text-foreground rounded-xl hover:border-accent/50 hover:text-accent transition-colors"
                          >
                            <FileText className="w-4 h-4" />
                            {tr("Download PDF Quote")}
                          </button>
                          <button
                            onClick={handleInlineSubmit}
                            disabled={submitStatus === "submitting"}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-medium btn-primary rounded-xl transition-colors glow-teal disabled:opacity-60"
                          >
                            {submitStatus === "submitting" ? (
                              <>
                                <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                                {tr("Sending...")}
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                {tr("Send Quote Request")}
                              </>
                            )}
                          </button>
                        </div>

                        {submitStatus === "error" && (
                          <p className="text-xs text-red-400 mt-2 text-center">
                            {tr("Failed to send. Please try again or")} <button onClick={sendToContactForm} className="underline hover:text-red-300">{tr("use the contact form")}</button>.
                          </p>
                        )}

                        {/* #10 — Shareable link */}
                        <div className="mt-3 flex justify-center">
                          <button
                            onClick={copyShareableLink}
                            className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-accent transition-colors"
                          >
                            {linkCopied ? <Check className="w-3 h-3 text-emerald-400" /> : <Link2 className="w-3 h-3" />}
                            {linkCopied ? tr("Link copied!") : tr("Copy shareable link")}
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Navigation (hidden on review step) */}
              {currentStepDef?.id !== "review" && (
                <div className="flex items-center justify-between mt-8 pt-5 border-t border-border">
                  <button
                    onClick={prevStep}
                    disabled={step === 0}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={tr("Back to previous step")}
                  >
                    <ChevronLeft className="w-4 h-4" /> {tr("Back")}
                  </button>
                  <button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="flex items-center gap-1.5 text-sm font-medium text-accent hover:text-accent/80 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={step === totalSteps - 2 ? tr("Review your configuration") : tr("Continue to next step")}
                  >
                    {step === totalSteps - 2 ? tr("Review") : tr("Continue")}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Right sidebar — Live pricing terminal readout ── */}
          <div className="space-y-4">
            {/*
              Terminal-style summary panel (feat/hero-redesign, Task 4).
              Replaces three separate bento-cards (Price, Quick-stats grid,
              Latency) with one compact monospace readout inside a 0.5px
              border box — the “$ route … / latency … / <capacity> <product>
              … / ready …” layout the brief specifies.

              Values remain bound to live configurator state (nothing is
              hardcoded) — this is a restyle of the same numbers, not a
              second source. Product code and label are derived from the
              existing PRODUCTS registry so a rename there flows through.
              The strikethrough/savings/IRU/enterprise/submarine badges and
              the “Indicative · Depends on route” disclaimer are preserved
              from the previous layout so nothing goes missing.

              Rendered in var(--font-mono) via `font-mono`, 12–13px,
              line-height 1.9 for a spacious readout feel. $ prompt in
              --accent, labels in --muted, values in --text-primary — as
              specified in the brief.
            */}
            {(() => {
              // Product code shown after the capacity value on the price
              // line: "1G ip", "10G l2", "100G dwdm", "df" (dark fiber).
              const productCode =
                state.product === "ip-transit"
                  ? "ip"
                  : state.product === "l2vpn"
                    ? "l2"
                    : state.product === "dwdm"
                      ? "dwdm"
                      : "df";

              // Left-column label used on the price line. Mirrors the
              // previous “Starting From / Estimated / IRU” discriminator so
              // no information is lost, just re-slotted into the readout.
              const priceLineLabel = pricing.isFrom
                ? state.product === "dark-fiber"
                  ? `${(state.darkFiberMeters / 1000).toFixed(1)}km ${productCode}`
                  : `${bw.label} ${productCode}`
                : state.product === "dark-fiber" && state.darkFiberModel === "iru"
                  ? `iru 15yr`
                  : `${bw.label} ${productCode}`;

              const originCity = city(POP_NODES.find((n) => n.id === state.origin)?.city ?? state.origin);
              const destCity = city(POP_NODES.find((n) => n.id === state.destination)?.city ?? state.destination);

              return (
                <div
                  className="bento-card"
                  style={{
                    // Explicit 0.5px border per the brief. bento-card’s own
                    // border is 1px; overriding here keeps the readout
                    // visually distinct from surrounding cards.
                    border: "0.5px solid var(--border-color)",
                    borderRadius: "var(--radius)",
                  }}
                >
                  <div className="relative z-10">
                    <div
                      className="font-mono text-[12px] sm:text-[13px] leading-[1.9] tabular-nums"
                      // aria-live so screen readers announce price changes as
                      // the user tweaks capacity/route — was implicit before,
                      // making it explicit avoids silent re-renders here.
                      aria-live="polite"
                    >
                      {needsRouteStep && (
                        <div className="flex gap-3">
                          <span className="text-accent select-none">$</span>
                          <span className="text-muted">{tr("route")}</span>
                          <span className="text-text-primary lowercase">
                            {originCity} → {destCity}
                          </span>
                        </div>
                      )}
                      {needsRouteStep && currentLatency && (
                        <div className="flex gap-3">
                          <span className="opacity-0 select-none">$</span>
                          <span className="text-muted w-16 shrink-0">{tr("latency")}</span>
                          <span className="text-text-primary">{currentLatency} ms</span>
                        </div>
                      )}
                      <div className="flex gap-3">
                        <span className="opacity-0 select-none">$</span>
                        <span className="text-muted w-16 shrink-0">{priceLineLabel}</span>
                        <span className="text-text-primary">
                          €{pricing.price.toLocaleString()}
                          <span className="text-muted">{tr(pricing.unit)}</span>
                        </span>
                      </div>
                      <div className="flex gap-3">
                        <span className="opacity-0 select-none">$</span>
                        <span className="text-muted w-16 shrink-0">{tr("ready")}</span>
                        <span className="text-text-primary">{leadTime(state.product)}</span>
                      </div>
                    </div>

                    {/* Strikethrough base + savings badge (preserved). */}
                    {state.product !== "dark-fiber" && hasDiscount && pricing.base !== pricing.price && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground line-through font-mono">
                          €{Math.round(pricing.base * pricing.redundancyMultiplier).toLocaleString()}
                        </span>
                        {totalSavePct > 0 && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400">
                            {tr("Save")} {totalSavePct}%
                          </span>
                        )}
                      </div>
                    )}

                    {/* IRU O&M line (preserved). */}
                    {state.product === "dark-fiber" && state.darkFiberModel === "iru" && (
                      <p className="mt-2 text-xs text-accent font-mono">
                        + €{Math.round(state.darkFiberMeters * 1.0 * 0.045).toLocaleString()}{tr("/yr O&M")}
                      </p>
                    )}

                    {/* Enterprise + submarine badges (preserved). */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {isEnterprise && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[9px] font-bold">
                          <Cpu className="w-2.5 h-2.5" /> {tr("Enterprise")}
                        </div>
                      )}
                      {state.product !== "dark-fiber" && state.product !== "ip-transit" && (
                        <SubmarineCableBadge origin={state.origin} destination={state.destination} compact />
                      )}
                    </div>

                    {/* Disclaimer — preserved verbatim per the brief. */}
                    <p className="mt-3 text-[10px] text-muted">
                      {pricing.isFrom ? tr("Indicative · Depends on route & contract") : tr("Indicative · Final quote may vary")}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Commitment card — unchanged (kept below the terminal readout
                since it is a lever the user directly controls in the panel,
                not a computed output). */}
            {state.product !== "dark-fiber" && (
              <div className="bento-card text-center py-3">
                <div className="relative z-10">
                  <div className="text-2xl font-bold font-mono text-foreground">{state.commitment}<span className="text-sm font-normal text-muted-foreground ml-1">{tr("mo")}</span></div>
                  <div className="text-[9px] text-muted-foreground mt-0.5">{tr("Commitment")}</div>
                  {state.commitment > 12 && (
                    <div className="mt-1 text-[10px] font-bold text-emerald-400">
                      -{Math.round((1 - pricing.commitmentMultiplier) * 100)}% {tr("discount")}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* #4 — Compare panel */}
            {showCompare && compareConfig && comparePricing && (
              <div className="bento-card">
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{tr("Saved Config")}</span>
                    <button onClick={() => { setShowCompare(false); setCompareConfig(null); }} className="text-[10px] text-muted-foreground hover:text-foreground">✕</button>
                  </div>
                  <div className="text-xs font-semibold text-foreground mb-1">{tr(PRODUCTS.find((p) => p.id === compareConfig.product)?.label ?? "")}</div>
                  <div className="space-y-1">
                    {compareConfig.product !== "dark-fiber" && (
                      <div className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">{tr("Capacity")}</span>
                        <span className="font-mono text-foreground">{getBwSteps(compareConfig.product)[compareConfig.bwIndex]?.label ?? "—"}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-[10px]">
                      <span className="text-muted-foreground">{tr("Price")}</span>
                      <span className="font-mono text-accent">€{comparePricing.price.toLocaleString()}{tr(comparePricing.unit)}</span>
                    </div>
                    {pricing.price !== comparePricing.price && (
                      <div className="flex justify-between text-[10px] pt-1 border-t border-border">
                        <span className="text-muted-foreground">{tr("Difference")}</span>
                        <span className={`font-mono font-bold ${pricing.price < comparePricing.price ? "text-emerald-400" : "text-amber-400"}`}>
                          {pricing.price < comparePricing.price ? "−" : "+"}€{Math.abs(pricing.price - comparePricing.price).toLocaleString()}{tr("/mo")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        </motion.div>
      </div>

      {/* ── Mobile sticky price bar — only visible while configurator section in viewport ── */}
      {/* When out of viewport: `inert` removes the bar (and its focusable buttons)
       * from the accessibility tree and from sequential focus, fixing the
       * aria-hidden-focus a11y violation (focusable descendants inside
       * aria-hidden region). `inert` is supported in all modern browsers
       * and React 19+. We keep aria-hidden as a fallback. */}
      <div
        ref={stickyBarRef}
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-accent/30 bg-background/95 backdrop-blur-xl px-4 py-2.5 transition-transform duration-300 ${
          inViewport ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}
        aria-hidden={!inViewport}
        inert={!inViewport}
      >
        <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
          <div className="flex flex-col min-w-0">
            <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground truncate">
              {pricing.isFrom ? tr("Starting from") : tr("Estimated")}
            </span>
            <span className="text-base font-bold font-mono text-accent truncate">
              €{pricing.price.toLocaleString()}<span className="text-[10px] text-muted-foreground ml-0.5">{tr(pricing.unit)}</span>
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="px-3 py-2 rounded-lg text-xs font-medium border border-border text-foreground active:scale-95 transition-transform"
                aria-label={tr("Previous step")}
              >
                {tr("Back")}
              </button>
            )}
            {step < totalSteps - 1 && (
              <button
                onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
                className="px-4 py-2 rounded-lg text-xs font-semibold bg-accent text-on-accent active:scale-95 transition-transform inline-flex items-center gap-1"
              >
                {step === totalSteps - 2 ? tr("Review") : tr("Continue")}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Step Header helper ── */
function StepHeader({ icon: Icon, title, subtitle }: { icon: typeof Zap; title: string; subtitle: string }) {
  const { language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground">{tr(title)}</h3>
        <p className="text-sm text-muted-foreground">{tr(subtitle)}</p>
      </div>
    </div>
  );
}

/* ── Summary Row helper ── */
function SummaryRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  const { language } = useTranslation();
  const tr = makeTr(language, CONFIGURATOR_BG);
  return (
    <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground">{tr(label)}</span>
      <span className={`text-xs font-medium font-mono ${accent ? "text-accent" : "text-foreground"}`}>{tr(value)}</span>
    </div>
  );
}
